import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError, call, upload } from "../api/client";
import { keys, useMapImages, useSystems } from "../api/queries";
import type { MapImageView, SystemInfo } from "../api/types";
import { MapView } from "../components/MapView";
import { dateTime, text } from "../text";

/** Admin page: upload the map images the rule data refers to. They come from the admin's own books and never ship with the app. */
export function MapsPage() {
  const systems = useSystems();
  return (
    <div className="stack">
      <h2>Karten</h2>
      <p className="muted">
        Kartenbilder stammen aus deinen eigenen Büchern und sind nicht Teil der App. Lade hier je Karte ein Bild (PNG, JPEG oder WebP) hoch; alle angemeldeten Nutzer sehen es
        dann. Die Umrisse in den Regeldaten beziehen sich auf die genannte Bildgröße.
      </p>
      {systems.data?.map((system) => <SystemMaps key={system.id} system={system} />)}
    </div>
  );
}

function SystemMaps({ system }: { system: SystemInfo }) {
  const maps = useMapImages(system.id);
  if (!maps.data?.length) return null;
  return (
    <section className="stack">
      <h3>{text(system.name)}</h3>
      <ul className="map-list">
        {maps.data.map((map) => (
          <MapCard key={map.id} system={system.id} map={map} />
        ))}
      </ul>
    </section>
  );
}

function MapCard({ system, map }: { system: string; map: MapImageView }) {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const name = text(map.name);

  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    setBusy(true);
    try {
      await action();
      await client.invalidateQueries({ queryKey: keys.maps(system) });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="card stack" data-map={map.id}>
      <header className="row spread">
        <h4>
          {name} {map.uploaded ? <span className="badge">vorhanden</span> : <span className="badge warning">kein Bild</span>}
        </h4>
        <span className="muted small">
          {map.id} · erwartet {map.width} × {map.height}
        </span>
      </header>
      {map.uploaded && (
        <p className="small">
          Bild {map.imageWidth} × {map.imageHeight}
          {map.updatedAt && <span className="muted"> · hochgeladen {dateTime(map.updatedAt)}</span>}
        </p>
      )}
      {map.warning && <p className="card warning-box small" role="alert">{map.warning}</p>}
      {error && <p className="error">{error}</p>}
      <MapView system={system} map={map.id} areas={[]} label={`Karte ${name}`} />
      <div className="row">
        <label className="button secondary">
          {map.uploaded ? "Bild ersetzen …" : "Bild hochladen …"}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            disabled={busy}
            aria-label={`Bild für ${name}`}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) void run(() => upload(`/api/systems/${encodeURIComponent(system)}/maps/${encodeURIComponent(map.id)}/image`, file));
            }}
          />
        </label>
        {map.uploaded && (
          <button
            type="button"
            className="link danger"
            disabled={busy}
            onClick={() =>
              window.confirm(`Bild der Karte ${name} löschen?`) &&
              void run(() => call(api.DELETE("/api/systems/{system}/maps/{map}/image", { params: { path: { system, map: map.id } } })))
            }
          >
            Bild löschen
          </button>
        )}
      </div>
    </li>
  );
}
